import { createContext, useContext, useId, useMemo } from "react";
import { useDateFormatter } from "react-aria";
import {
  CalendarDate,
  isSameDay,
  parseAbsolute,
  parseDate,
  toTime,
  toZoned,
  ZonedDateTime,
} from "@internationalized/date";
import { invariant } from "@pexip-engage/utils";
import { useLoaderData } from "@remix-run/react";

import type { TimetableLoaderData } from "~/modules/timetable/timetable.loader.server";
import { useRootLoaderData } from "~/root";
import {
  DayParts,
  endOfDay,
  getDayInterval,
  getDayPart,
  isBetween,
  useTimeZone,
} from "~/utils/date";
import { ALL_TIMEZONES } from "~/utils/timezones.utils";

export function useTimetableRouteContext() {
  const prefetchId = useId();
  const props = useLoaderData<TimetableLoaderData>();
  const SERVER_TIME_ZONES = useRootLoaderData()?.SERVER_TIME_ZONES;

  const timeZone = useTimeZone({ officeTimeZone: props.office.timeZone });

  const context = useMemo(
    () => ({
      start: parseDate(props.start),
      end: parseDate(props.end),
      errors: props.errors,
      timeZone,
      prefetchId,
      TIME_ZONES: ALL_TIMEZONES.length === 0 ? SERVER_TIME_ZONES ?? [] : ALL_TIMEZONES,
    }),
    [props.start, props.end, props.errors, timeZone, SERVER_TIME_ZONES, prefetchId],
  );

  return context;
}

export function useTimetableData({ end, start, timetableData, ...props }: TimetableLoaderData) {
  const { timeZone } = useTimetableContext();
  const period = usePeriod({ start: new Date(start), end: new Date(end) });

  const slots = useMemo(() => {
    const startDateParam = toZoned(parseDate(start), timeZone);
    const endDateParam = endOfDay(toZoned(parseDate(end), timeZone));

    const allAvailableSlots = Object.values(timetableData)
      .flat()
      .map((slot) => ({
        ...slot,
        start: parseAbsolute(slot.start, timeZone),
        end: parseAbsolute(slot.end, timeZone),
      }))
      .filter((slot) => isBetween(slot.start, startDateParam, endDateParam));

    const slots: TimetableGroups = {
      [DayParts.MORNING]: {
        id: DayParts.MORNING,
        timeframes: [],
      },
      [DayParts.NOON]: {
        id: DayParts.NOON,
        timeframes: [],
      },
      [DayParts.EVENING]: {
        id: DayParts.EVENING,
        timeframes: [],
      },
    };

    const days = getDayInterval(startDateParam, endDateParam).map((day) => ({
      id: day.toAbsoluteString(),
      date: day,
    }));

    allAvailableSlots.forEach((slot) => {
      const dayPart = getDayPart(slot.start.hour);

      const slotStartHour = toTime(slot.start).toString();
      const slotEndHour = toTime(slot.end).toString();

      let timeframe = slots[dayPart].timeframes.find(
        (timeframe) => timeframe.startHour === slotStartHour && timeframe.endHour === slotEndHour,
      );

      if (!timeframe) {
        const timeframeId = `${dayPart}-${slotStartHour}-${slotEndHour}`;

        timeframe = {
          id: timeframeId,
          startHour: slotStartHour,
          endHour: slotEndHour,
          timeSlots: days.map((day) => ({ id: `${timeframeId}-${day.id}`, day })),
        };

        slots[dayPart].timeframes.push(timeframe);
      }

      const slotIdx = timeframe.timeSlots.findIndex((timeSlot) =>
        isSameDay(timeSlot.day.date, slot.start),
      );

      const timeslotData = timeframe.timeSlots[slotIdx];
      invariant(timeslotData, "Timeslot data not found");

      timeframe.timeSlots[slotIdx] = {
        ...timeslotData,
        data: {
          start: slot.start,
          end: slot.end,
          employeeIds: slot.contacts.map((c) => c.id),
        },
      };
    });

    // Sort the timeframes by start hour.
    Object.values(DayParts).forEach((dayPart) => {
      slots[dayPart].timeframes.sort(({ startHour: a }, { startHour: b }) =>
        a < b ? -1 : a > b ? 1 : 0,
      );
    });

    return {
      days,
      groups: slots,
      start: startDateParam,
    };
  }, [end, start, timetableData, timeZone]);

  return {
    period,
    ...props,
    ...slots,
  };
}

function usePeriod({ end, start }: { start: Date; end: Date }) {
  const { timeZone } = useTimetableContext();
  const formatter = useDateFormatter({ timeZone, dateStyle: "long" });

  return formatter.formatRange(start, end);
}

export interface TimetableGroups {
  [DayParts.MORNING]: TimetableGroup;
  [DayParts.NOON]: TimetableGroup;
  [DayParts.EVENING]: TimetableGroup;
}

interface TimetableGroup {
  id: DayParts;
  timeframes: Timeframe[];
}

export interface Timeframe {
  id: string;
  timeSlots: TimetableSlot[];
  startHour: string;
  endHour: string;
}

export interface TimetableSlot {
  id: string;

  day: {
    id: string;
    date: ZonedDateTime;
  };

  data?: {
    end: ZonedDateTime;
    start: ZonedDateTime;
    employeeIds: string[];
  };
}

const TimetableContext = createContext<{
  errors: {
    OUT_OF_RANGE: boolean;
    TIMESLOT_NOT_AVAILABLE: boolean;
  };
  prefetchId: string;
  start: CalendarDate;
  end: CalendarDate;
  timeZone: string;
  TIME_ZONES: string[];
} | null>(null);

export const TimetableContextProvider = TimetableContext.Provider;

export function useTimetableContext() {
  const ctx = useContext(TimetableContext);

  invariant(ctx, "No TimetableContextProvider found");

  return ctx;
}
